import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { useLookupRegistrationsQuery } from '../../../../services/RegistrationService';
import type { RegistrationType } from '../../../../services/RegistrationService/types/registrationTypes';
import RegistrationLookupResultsGrid from '../grids/RegistrationLookupResultsGrid';

interface SearchPatientsButtonProps {
  company?: string | null;
  center?: string | null;
}

const SearchPatientsButton: React.FC<SearchPatientsButtonProps> = ({ company, center }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [shouldSearch, setShouldSearch] = useState(false);

  const {
    data: registrations,
    isLoading,
    error,
  } = useLookupRegistrationsQuery(
    { 
      company: company || '', 
      center: center || '', 
      searchTerm 
    },
    {
      skip: !shouldSearch || !company || !center || !searchTerm,
    }
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
    setShouldSearch(false);
  };

  const handleSelectPatient = (registration: RegistrationType) => {
    setOpen(false);
    // Navigate to encounter create with the selected patient's MRN
    const mrn = registration.identifiers?.mrn;
    if (mrn) {
      const params = [];
      if (company) params.push(`company=${encodeURIComponent(company)}`);
      if (center) params.push(`center=${encodeURIComponent(center)}`);
      params.push(`mrn=${encodeURIComponent(mrn)}`);
      const query = params.length > 0 ? `?${params.join('&')}` : '';
      navigate(`/EncounterCreate${query}`);
    }
  };

  const handleCreateNewPatient = () => {
    setOpen(false);
    const params = [];
    if (company) params.push(`company=${encodeURIComponent(company)}`);
    if (center) params.push(`center=${encodeURIComponent(center)}`);
    const query = params.length > 0 ? `?${params.join('&')}` : '';
    navigate(`/RegistrationCreate${query}`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setShouldSearch(false);
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setShouldSearch(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const canSearch = Boolean(company && center);

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        New Encounter
      </Button>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#ffffff',
          borderBottom: '2px solid #3b82f6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2.5,
          fontWeight: 600,
          color: '#1e293b'
        }}>
          Search for an Existing Patient
          <IconButton 
            onClick={handleClose}
            sx={{ color: '#64748b' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Search Section */}
          <Box sx={{ mb: 3 }}>
            {company && center ? (
              <>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#64748b' }}>
                  Searching in {company} - {center}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      autoFocus
                      label="Search Patient (MRN or Name)"
                      size="small"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Enter MRN or patient name"
                      disabled={isLoading}
                      sx={{ width: 400 }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      disabled={!searchTerm.trim() || isLoading}
                      sx={{
                        backgroundColor: '#162346',
                        '&:hover': { backgroundColor: '#1a2a52' },
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      Search
                    </Button>
                  </Stack>
                </Box>
              </>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please select a company and center before searching for patients.
              </Alert>
            )}
          </Box>

          {/* Divider */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            my: 3,
            '&::before, &::after': {
              content: '""',
              flex: 1,
              height: '1px',
              backgroundColor: '#e2e8f0',
            }
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                px: 2, 
                color: '#64748b', 
                fontWeight: 600, 
                letterSpacing: 1 
              }}
            >
              OR
            </Typography>
          </Box>

          {/* Create New Patient Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Button
              onClick={handleCreateNewPatient}
              variant="contained"
              sx={{
                backgroundColor: '#43a047',
                color: '#fff',
                fontWeight: 600,
                minWidth: 200,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#388e3c'
                }
              }}
            >
              Create New Patient
            </Button>
          </Box>

          {/* Loading State */}
          {isLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, color: '#64748b' }}>
                Searching patients...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error searching patients. Please try again.
            </Alert>
          )}

          {/* Results Grid */}
          {shouldSearch && registrations && !isLoading && (
            <RegistrationLookupResultsGrid
              registrations={registrations}
              isLoading={isLoading}
              onSelectRegistration={handleSelectPatient}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button 
            onClick={handleClose}
            sx={{ color: '#64748b' }}
          >
            Cancel
          </Button>
          {shouldSearch && registrations && registrations.length > 0 && (
            <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
              Click on a row to select a patient and create an encounter
            </Typography>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SearchPatientsButton;
